import { useState } from "react";
import { ChevronRight } from "lucide-react";

const SEG_COLORS = ["#FF1A82", "#10B981", "#8E9775", "#C77D9E"];

export function StatGroup({ title, items }) {
  const [open, setOpen] = useState(false);
  const entries = items && typeof items === "object" ? Object.entries(items) : [];
  if (entries.length === 0) return null;
  const voted = entries.filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const zeros = entries.filter(([, v]) => !(v > 0));
  const testKey = title.replace(/\s+/g, "-").toLowerCase();

  return (
    <div data-testid={`statgroup-${testKey}`}>
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#10B981]">{title}</div>

      {voted.length === 0 ? (
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-bold tabular-nums text-[#9CA3AF] text-xl">0%</span>
          <span className="text-[#9CA3AF]">{entries[0][0]}</span>
        </div>
      ) : (
        <div className="mt-2 space-y-1">
          {voted.map(([label, v], i) => (
            <div key={label} className="flex items-baseline gap-2" data-testid={`statrow-${testKey}-${i}`}>
              <span className="font-bold tabular-nums text-xl shrink-0" style={{ color: SEG_COLORS[i % SEG_COLORS.length] }}>{v}%</span>
              <span className="text-[#1A1A1A] leading-snug min-w-0">{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* One compact segmented bar */}
      <div className="mt-2 h-2.5 rounded-full bg-black/10 overflow-hidden flex" data-testid={`statbar-${testKey}`}>
        {voted.map(([label, v], i) => (
          <div key={label} className="h-full stat-bar" style={{ width: `${v}%`, background: SEG_COLORS[i % SEG_COLORS.length] }} />
        ))}
      </div>

      {zeros.length > 0 && (
        <>
          <button onClick={() => setOpen(o => !o)} data-testid={`statgroup-toggle-${testKey}`}
            className="mt-1.5 inline-flex items-center gap-0.5 text-sm font-medium text-[#666666] hover:text-[#10B981] transition-colors">
            View all answers <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
          {open && (
            <div className="mt-1.5 space-y-1" data-testid={`statgroup-all-${testKey}`}>
              {entries.sort((a, b) => b[1] - a[1]).map(([label, v]) => (
                <div key={label} className="flex items-baseline justify-between gap-3">
                  <span className={`leading-snug min-w-0 ${v > 0 ? "text-[#1A1A1A]" : "text-[#9CA3AF]"}`}>{label}</span>
                  <span className={`font-semibold tabular-nums shrink-0 text-right ${v > 0 ? "text-[#1A1A1A]" : "text-[#9CA3AF]"}`}>{v}%</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function StatCard({ title, children, accent = "lime" }) {
  const ring = accent === "pink" ? "ring-[#10B981]/30" : "ring-[#FF1A82]/30";
  return (
    <div className={`rounded-2xl bg-white/70 backdrop-blur-lg border border-black/10 p-5 sm:p-6 ring-1 ${ring}`}>
      <h3 className="font-heading text-2xl mb-4 text-[#1A1A1A]">{title}</h3>
      {children}
    </div>
  );
}
